import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { listDevAccounts, removeDevAccount, saveDevAccount, type DevAccountRow } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function DevAccountsRoute() {
  const qc = useQueryClient();
  const [playerId, setPlayerId] = useState("");
  const [note, setNote] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState("");
  const query = useQuery({ queryKey: ["dev-accounts"], queryFn: listDevAccounts });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["dev-accounts"] });
    void qc.invalidateQueries({ queryKey: ["flags"] });
  };
  const save = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) => saveDevAccount(id, value),
    onSuccess: (_result, variables) => {
      if (variables.id === editingId) {
        setEditingId(null);
        setEditNote("");
      } else {
        setPlayerId("");
        setNote("");
      }
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: removeDevAccount, onSuccess: refresh });

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    const id = playerId.trim();
    if (!id || save.isPending) return;
    save.mutate({ id, value: note.trim() });
  }

  function handleRemove(row: DevAccountRow) {
    if (!confirm(`Remove ${row.display_name ?? row.player_id} from developer accounts?`)) return;
    remove.mutate(row.player_id);
  }

  const rows = query.data ?? [];
  return <AppShell title="Dev accounts" subtitle="Exclude developer accounts from anti-cheat flags">
    <div className="max-w-5xl space-y-5">
      <Alert><AlertDescription className="text-xs">
        Developer accounts skip save-check and player-report flags. Duplicate purchase evidence is still recorded and visible. Existing flags remain in history and can be shown from the Flags page. Manual bans still apply. This list does not grant admin access or change purchases.
      </AlertDescription></Alert>

      <form onSubmit={handleAdd} className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h2 className="text-sm font-medium">Add developer account</h2>
        <div className="flex flex-wrap gap-2">
          <Input aria-label="Player ID" value={playerId} onChange={(event) => setPlayerId(event.target.value)} placeholder="Player ID" className="font-mono min-w-52 flex-1" required />
          <Input aria-label="Note" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Note (optional)" className="min-w-52 flex-1" />
          <Button type="submit" disabled={!playerId.trim() || save.isPending || remove.isPending}>{save.isPending && editingId === null ? "Saving…" : "Add"}</Button>
        </div>
        <p className="text-xs text-muted-foreground">Adding a player already on this list updates their note.</p>
        {save.error && editingId === null && <Alert variant="destructive"><AlertDescription>{message(save.error)}</AlertDescription></Alert>}
      </form>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <h2 className="text-sm font-medium">Whitelisted players</h2>
          <span className="text-xs text-muted-foreground">{rows.length} account{rows.length === 1 ? "" : "s"}</span>
        </div>
        {query.error && <div className="p-4"><Alert variant="destructive"><AlertDescription>{message(query.error)}</AlertDescription></Alert></div>}
        {remove.error && <div className="p-4"><Alert variant="destructive"><AlertDescription>{message(remove.error)}</AlertDescription></Alert></div>}
        {query.isPending ? <div className="p-4"><Skeleton className="h-24 w-full" /></div> : rows.length === 0 ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">No developer accounts yet.</div> :
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground"><tr>
              <th className="px-3 py-2 text-left">Player</th>
              <th className="px-3 py-2 text-left">Note</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Added</th>
              <th className="px-3 py-2 text-left">Added by</th>
              <th className="px-3 py-2"><span className="sr-only">Actions</span></th>
            </tr></thead>
            <tbody>{rows.map((row) => <tr key={row.player_id} className="border-t border-border/50">
              <td className="px-3 py-3"><Link to={`/players/${encodeURIComponent(row.player_id)}`} className="text-blue-300 hover:underline">
                <span className="block text-xs">{row.display_name ?? row.player_id}</span>
                {row.display_name && <span className="block font-mono text-[11px] text-muted-foreground">{row.player_id}</span>}
              </Link></td>
              <td className="px-3 py-2 text-xs min-w-44">{editingId === row.player_id ? <div className="space-y-2">
                <Input aria-label={`Note for ${row.player_id}`} value={editNote} onChange={(event) => setEditNote(event.target.value)} className="h-8" />
                <div className="flex gap-1"><Button size="sm" onClick={() => save.mutate({ id: row.player_id, value: editNote.trim() })} disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button><Button size="sm" variant="ghost" onClick={() => { setEditingId(null); save.reset(); }}>Cancel</Button></div>
                {save.error && <span className="text-destructive">{message(save.error)}</span>}
              </div> : <button type="button" className="text-left text-muted-foreground hover:text-foreground" onClick={() => { save.reset(); setEditingId(row.player_id); setEditNote(row.note ?? ""); }} title="Edit note">{row.note || <span className="italic">Add note</span>}</button>}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatTimestamp(row.added_at)}</td>
              <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{row.added_by ?? "—"}</td>
              <td className="px-3 py-2 text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" title="Remove" aria-label={`Remove ${row.player_id}`} disabled={remove.isPending || save.isPending} onClick={() => handleRemove(row)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
            </tr>)}</tbody>
          </table>}
      </div>
    </div>
  </AppShell>;
}
